import React, { useState } from "react";
import { Button, Card, Stack } from "react-bootstrap";
import { patchMember, patchGuardian } from "../../../../services/httpClient";
import { propertyAddressLine } from "../../../../helpers/housingAssignment";
import CaseHousingFlow from "../../../ui/CaseHousingFlow";

interface CaseHousingState {
  caseId: any;
  caseName: string;
  caseLevelHousing?: string;
  people: any[];
  dwellings: any[];
  facilities: any[];
  rooms: any[];
  beds: any[];
  myCase: boolean;
  isOpen: boolean;
  setIsOpen: any;
  onChanged: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

// The Household tab's housing summary. The flow it opens is shared with the Shelter tab.
const CaseHousing = ({
  caseId,
  caseName,
  caseLevelHousing,
  people,
  dwellings,
  facilities,
  rooms,
  beds,
  myCase,
  isOpen,
  setIsOpen,
  onChanged,
  setShowToast,
  setToastText,
  setToastBody,
}: CaseHousingState) => {
  const [errMsg, setErrMsg] = useState("");

  const numericCaseId = Number(caseId);
  const residentialPeople = people.filter((p: any) => p.is_residential === 1);
  const everyoneResidential = residentialPeople.length === people.length;

  const housedDwelling = dwellings.find((d: any) => Number(d.occupant_case_id) === numericCaseId);
  const housedRoom = rooms.find((r: any) => Number(r.family_case_id) === numericCaseId);
  const placedCount = residentialPeople.filter((p: any) =>
    beds.some((b: any) => b.occupant_type === p.type && b.occupant_id === p.id),
  ).length;

  const currentMode = housedDwelling ? "dwelling" : housedRoom ? "room" : placedCount > 0 ? "individual" : "";
  const summary = caseLevelHousing
    ? caseLevelHousing
    : placedCount > 0
      ? `Bed - ${placedCount} of ${residentialPeople.length} placed`
      : "Not housed";

  const summaryDetail = housedDwelling ? propertyAddressLine(housedDwelling) : "";

  // Marking a case residential means saving the flag on every active person on it - there is
  // no case-level column, so this is the case-level gesture. Nothing else sets it: the flag
  // says a person is meant to be housed and it sizes the place they need.
  const onClickSetAllResidential = () => {
    setErrMsg("");
    people
      .filter((p: any) => p.is_residential !== 1)
      .reduce(
        (chain: Promise<any>, p: any) =>
          chain.then(() =>
            p.type === "guardian"
              ? patchGuardian({ is_residential: true }, caseId, p.id)
              : patchMember({ is_residential: true }, caseId, p.id),
          ),
        Promise.resolve(),
      )
      .then(() => {
        setShowToast(true);
        setToastText("Marked Residential");
        setToastBody(caseName);
        onChanged();
      })
      .catch((err: any) => setErrMsg(err?.response?.data?.msg || "Could not update residential"));
  };

  if (isOpen) {
    return (
      <CaseHousingFlow
        caseId={caseId}
        caseName={caseName}
        people={people}
        dwellings={dwellings}
        facilities={facilities}
        rooms={rooms}
        beds={beds}
        backLabel="Back to Household"
        onClose={() => setIsOpen(false)}
        onChanged={onChanged}
        setShowToast={setShowToast}
        setToastText={setToastText}
        setToastBody={setToastBody}
      />
    );
  }

  const setAllButton = myCase && !everyoneResidential && (
    <Button size="sm" className="app-secondary-color button flex-shrink-0" onClick={onClickSetAllResidential}>
      Set All Members Residential
    </Button>
  );

  // Nobody here is meant to be housed, so there is nothing to assign - just the way back in.
  if (residentialPeople.length === 0) {
    if (!myCase) return null;
    return (
      <>
        <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
          {errMsg}
        </p>
        <Card className="bg-light mb-3">
          <Stack direction="horizontal" gap={3} className="p-2 align-items-center flex-wrap">
            <span className="role-dot role-dot-blue"></span>
            <span className="small flex-grow-1">No one on this case is marked residential.</span>
            {setAllButton}
          </Stack>
        </Card>
      </>
    );
  }

  return (
    <>
      <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
        {errMsg}
      </p>
      <Card className="bg-light mb-3">
        <Stack direction="horizontal" gap={3} className="p-2 align-items-center flex-wrap">
          <span className="small fw-semibold">Housing</span>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="small">{summary}</div>
            {summaryDetail && <div className="small text-muted">{summaryDetail}</div>}
          </div>
          {setAllButton}
          {myCase && (
            <Button size="sm" className="app-primary-color-inverse button flex-shrink-0" onClick={() => setIsOpen(true)}>
              {currentMode ? "Edit" : "Assign Case"}
            </Button>
          )}
        </Stack>
      </Card>
    </>
  );
};

export default CaseHousing;
